<template>
    <div id="new-ideasoft-channel-form">
        <a-form>
            <div class="form-wrapper">
                <a-alert message="Redirect URL" type="success">
                    <div slot="description">
                        <div class="webhook-info">
                            <span>
                                <a-icon type="copy" @click="copyWebhookAddress" />
                            </span>
                            <span>{{ getWebhookAddress() }}</span>
                        </div>
                    </div>
                </a-alert>
                <div class="input-wrapper d-none">
                    <label>{{ $t("whatsappChannel.channelName") }}</label>
                    <a-form-item
                        :validate-status="getErrorStatus('channelName')"
                        :help="getErrorMessage('channelName')"
                    >
                        <a-input
                            v-model="form.channelName"
                            :placeholder="$t('whatsappChannel.channelName')"
                            name="channelName"
                            ref="channelName"
                        />
                    </a-form-item>
                </div>
                <div class="input-wrapper">
                    <label>{{ $t("message.websiteAddress") }}</label>
                    <a-input
                        addon-before="https://"
                        addon-after=".myideasoft.com"
                        v-model="form.domain"
                        v-validate="'required'"
                        name="domain"
                    />
                </div>
                <div class="input-wrapper">
                    <label>Client ID</label>
                    <a-input
                        placeholder="Ideasoft API Client ID"
                        v-model="form.clientId"
                        v-validate="'required'"
                        name="clientId"
                    />
                </div>
                <div class="input-wrapper">
                    <label>Client Secret</label>
                    <a-input
                        placeholder="Ideasoft API Client Secret"
                        v-model="form.clientSecret"
                        v-validate="'required'"
                        name="clientSecret"
                    />
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <div class="actions">
                <a-button @click="handleCancel">{{ $t("message.cancel") }}</a-button>
                <a-button class="pr-confirm-btn" @click="handleSubmit" type="primary" :disabled="loading">
                    {{ $t("mail.connect") }}
                </a-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../config/config";
import { copyText2Clipboard } from "../../../../utils/utils";
import { socketMessageStream } from "../../../../utils/socketStream";
import { first, map, timeout } from "rxjs/operators";

export default {
    name: "NewOpenAIChannel",
    props: {
        channel: {
            type: Object,
            default: () => {},
        },
    },
    components: {},
    data() {
        return {
            form: {
                channelName: "Ideasoft",
                domain: "",
                clientId: "",
                clientSecret: "",
            },
            loading: false,
            clone: _.cloneDeep(this.channel),
            submitClicked: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site?._id,
        }),
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
        }),
        getWebhookAddress() {
            return `${CONFIG.SOCKET_URL}/webhook/oauth2/ideasoft`;
        },
        copyWebhookAddress() {
            copyText2Clipboard(this.getWebhookAddress());
            this.$message.success(this.$t("message.redirectURLcopiedToClipboard"));
        },
        getErrorStatus(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? "error" : "";
        },
        getErrorMessage(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? this.$t("message.requiredChannel") : "";
        },
        handleCancel() {
            this.$emit("cancel");
        },
        startOauth() {
            this.loading = true;
            const redirectUrl = this.getWebhookAddress();
            const url = `https://${this.form.domain}.myideasoft.com/panel/auth?response_type=code&client_id=${this.form.clientId}&state=${this.siteId}&redirect_uri=${redirectUrl}`;
            this.listenSocket();
            this.window = window.open(url, "_blank");
        },
        listenSocket() {
            socketMessageStream
                .pipe(
                    first((x) => x.cmd === "ideasoft_callback_response"),
                    map((x) => x.data),
                    timeout(30000)
                )
                .toPromise()
                .then(async (data) => {
                    const payload = {
                        domain: this.form.domain,
                        clientId: this.form.clientId,
                        clientSecret: this.form.clientSecret,
                        code: data.code,
                        accessToken: null,
                        refreshToken: null,
                    };
                    await this.completeCreateChannel(payload);
                    this.loading = false;
                    this.window.close();
                    this.$message.success(this.$t("message.ideasoftConnectedSucs"));
                })
                .catch((err) => {
                    this.loading = false;
                    this.window.close();
                    this.$message.error(this.$t("message.failedToConnectIdeasoft"));
                });
        },
        completeCreateChannel(payload) {
            const data = {
                payload,
                type: "ideasoft",
                name: "Ideasoft",
            };
            return this.createChannel(data)
                .then(() => {
                    this.$message.success(this.$t("message.channelCreatedSuccessfully"));
                    this.$emit("ok");
                })
                .catch((err) => {
                    this.$message.error(this.$t("message.failedToCreateChannel"));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleSubmit() {
            this.submitClicked = true;
            if (this.form.channelName.length < 1) {
                this.$refs.channelName.focus();
                return;
            } else {
                const isValid = true;
                if (isValid) {
                    this.startOauth();
                }
            }
        },
    },
    mounted() {},
    beforeDestroy() {},
};
</script>

<style lang="scss" scoped>
#new-ideasoft-channel-form {
    min-height: 250px;
    flex-direction: column;

    .form-wrapper {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .webhook-info {
            display: flex;
            gap: 5px;
            justify-content: center;
            padding: 5px;
            overflow-wrap: anywhere;

            .anticon {
                &:hover {
                    color: var(--color-text-info-hover);
                }
            }
        }

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;
            height: 75px;
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        padding: 10px;
        margin-top: 20px;
        background: var(--color-bg-default);
        border-top: 1px solid var(--color-border-default);
    }
}
</style>

<style lang="scss">
.new-sms-postacell {
    .custom-modal-body {
        padding: 0px !important;
    }
}
</style>
